import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BookOpen, Workflow, Bot, Copy, Check, Trash2, Zap, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { id: "notebook", label: "Notebook API", icon: BookOpen },
  { id: "workflow", label: "AI Workflow API", icon: Workflow },
  { id: "automation", label: "AI Automation", icon: Bot },
];

export const ApiKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [creating, setCreating] = useState(null);
  const [copied, setCopied] = useState(null);
  const [pinging, setPinging] = useState(null);

  const load = async () => { const res = await api.get("/keys"); setKeys(res.data); };
  useEffect(() => { load(); }, []);

  const generate = async (key_type) => {
    setCreating(key_type);
    try {
      await api.post("/keys", { key_type });
      await load();
      toast.success("API key generated & connected.");
    } catch { toast.error("Could not generate key."); }
    finally { setCreating(null); }
  };

  const copy = (k) => { navigator.clipboard.writeText(k.key); setCopied(k.key_id); setTimeout(() => setCopied(null), 1500); };

  const revoke = async (id) => { await api.delete(`/keys/${id}`); await load(); toast.success("Key revoked."); };

  const ping = async (k) => {
    setPinging(k.key_id);
    try {
      const res = await api.get("/v1/ping", { headers: { "X-API-Key": k.key } });
      toast.success(`Connection healthy · ${res.data.label} · ${res.data.usage} calls`);
      load();
    } catch { toast.error("Ping failed."); }
    finally { setPinging(null); }
  };

  return (
    <div className="h-full overflow-y-auto px-4 lg:px-10 py-8" data-testid="api-keys-panel">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-2xl font-bold text-white">API Keys & Connectors</h1>
        <p className="mt-2 text-slate-400 text-sm">Generate real, callable keys that auto-connect the twin to your notebooks, workflows and automations.</p>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {TYPES.map((t) => (
            <button key={t.id} onClick={() => generate(t.id)} disabled={creating === t.id} data-testid={`generate-${t.id}-btn`}
              className="group glass rounded-xl border border-slate-800 p-5 text-left hover:border-emerald-500/50 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <t.icon className="w-5 h-5 text-emerald-400" />
                {creating === t.id ? <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" /> : <span className="text-xs font-mono text-slate-500 group-hover:text-emerald-400 transition-colors">generate +</span>}
              </div>
              <h3 className="text-slate-100 font-medium text-sm">{t.label}</h3>
              <code className="text-[10px] font-mono text-slate-500">/api/v1/{t.id}</code>
            </button>
          ))}
        </div>

        <h2 className="mt-10 mb-4 text-sm font-mono uppercase tracking-widest text-slate-500">Your keys</h2>
        <div className="space-y-3">
          {keys.length === 0 && (
            <div className="glass rounded-xl border border-slate-800 p-8 text-center">
              <KeyRound className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No keys yet. Generate one above to connect the twin.</p>
            </div>
          )}
          {keys.map((k) => (
            <div key={k.key_id} data-testid={`key-row-${k.key_id}`} className="glass rounded-xl border border-slate-800 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 border border-emerald-500/30 rounded px-2 py-0.5">{k.label}</span>
                    <span className="text-xs text-slate-500 font-mono">{k.usage} calls</span>
                  </div>
                  <code className="mt-2 block text-xs font-mono text-slate-300 bg-slate-950 rounded px-3 py-2 border border-slate-800 truncate max-w-md">{k.key}</code>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => copy(k)} variant="outline" size="sm" data-testid={`copy-key-${k.key_id}`}
                    className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 h-9">
                    {copied === k.key_id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                  <Button onClick={() => ping(k)} disabled={pinging === k.key_id} size="sm" data-testid={`ping-key-${k.key_id}`}
                    className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 h-9">
                    {pinging === k.key_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Test
                  </Button>
                  <Button onClick={() => revoke(k.key_id)} variant="outline" size="sm" data-testid={`revoke-key-${k.key_id}`}
                    className="border-slate-700 bg-slate-900 text-slate-400 hover:text-red-400 hover:bg-slate-800 h-9">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 glass rounded-xl border border-slate-800 p-5">
          <h3 className="text-sm font-medium text-slate-200 mb-2">Call your connector</h3>
          <pre className="text-[12px] font-mono text-emerald-300 bg-slate-950 rounded-lg p-4 border border-slate-800 overflow-x-auto">
curl -X POST {process.env.REACT_APP_BACKEND_URL}/api/v1/notebook \{"\n"}
{"  "}-H "X-API-Key: YOUR_KEY" \{"\n"}
{"  "}-H "Content-Type: application/json" \{"\n"}
{"  "}-d '{"{"}"prompt":"Design a Redis leaderboard for a turn-based game"{"}"}'
          </pre>
        </div>
      </div>
    </div>
  );
};
